import { Icon } from "@bms-ai/bms-ai-ui-lib/components";
import { useState } from "react";
import FailIcon from "../../../_assets/icons/FailIcon";
import WarningIcon from "../../../_assets/icons/WarningIcon";
import { formatCheckId } from "../../../helpers/helpers";
import { formatMessage } from "../QAResultsNew";

interface Finding {
  finding_id: string;
  check_id: string;
  category: string;
  result: "PASS" | "FAIL" | "BRAND_GUIDELINE_NOT_AVAILABLE" | "NA";
  severity: "INFO" | "MINOR" | "MAJOR" | "CRITICAL";
  message: string;
  frame_id: string | null;
  doc_id: string | null;
  source_type: string | null;
  actual: string | null;
  expected: string | null;
  recommendation: string | null;
  used_llm: boolean;
  confidence: number | null;
}

interface ManuscriptSectionProps {
  manuscriptFindings: Finding[];
  expandedFindings: Set<string>;
  expandedFrames: Set<string>;
  setExpandedFindings: React.Dispatch<React.SetStateAction<Set<string>>>;
  setExpandedFrames: React.Dispatch<React.SetStateAction<Set<string>>>;
  isResultsComplete: boolean;
}

export const ManuscriptSection: React.FC<ManuscriptSectionProps> = ({
  manuscriptFindings,
  expandedFindings,
  expandedFrames,
  setExpandedFindings,
  setExpandedFrames,
  isResultsComplete,
}) => {
  if (manuscriptFindings.length === 0) return null;

  // Group findings by check_id
  const groupedByCheck = manuscriptFindings.reduce(
    (acc, finding) => {
      if (!acc[finding.check_id]) {
        acc[finding.check_id] = [];
      }
      acc[finding.check_id].push(finding);
      return acc;
    },
    {} as Record<string, Finding[]>,
  );

  // Determine which findings to show
  const selectedKey =
    expandedFindings.size > 0 ? Array.from(expandedFindings)[0] : null;
  let selectedFindingsComponent: React.ReactNode = null;

  if (selectedKey && selectedKey.startsWith("check_")) {
    const checkId = selectedKey.replace("check_", "");
    const checkFindings = groupedByCheck[checkId];
    if (checkFindings) {
      selectedFindingsComponent = <CheckFindings findings={checkFindings} isResultsComplete={isResultsComplete} />;
    }
  }

  return (
    <div className="source-type-group">
      {/* <div className="source-type-header">
        <Headline3>Manuscript</Headline3>
      </div> */}

      <div className="card-based-section">
        <div className="cards-container-left">
          {Object.entries(groupedByCheck).map(([checkId, findings]) => (
            <ManuscriptCheckGroup
              key={checkId}
              checkId={checkId}
              findings={findings}
              expandedFindings={expandedFindings}
              expandedFrames={expandedFrames}
              setExpandedFindings={setExpandedFindings}
              setExpandedFrames={setExpandedFrames}
            />
          ))}
        </div>

        {/* {!isResultsComplete && (
          <div style={{ textAlign: "center", color: "#999" }}>
            <div className="skeleton-finding-card">
              <div className="skeleton-label" style={{ marginBottom: "8px" }}></div>
              <div className="skeleton-text" style={{ width: "80%", marginBottom: "8px" }}></div>
              <div className="skeleton-text" style={{ width: "60%" }}></div>
            </div>
          </div>
        )} */}

        {selectedFindingsComponent && (
          <div className="findings-container-right">
            {selectedFindingsComponent}
          </div>
        )}
      </div>
    </div>
  );
};

// Manuscript check group component with card-based selection
const ManuscriptCheckGroup: React.FC<{
  checkId: string;
  findings: Finding[];
  expandedFindings: Set<string>;
  expandedFrames: Set<string>;
  setExpandedFindings: React.Dispatch<React.SetStateAction<Set<string>>>;
  setExpandedFrames: React.Dispatch<React.SetStateAction<Set<string>>>;
}> = ({
  checkId,
  findings,
  expandedFindings,
  expandedFrames: _expandedFrames,
  setExpandedFindings,
  setExpandedFrames,
}) => {
  const checkKey = `check_${checkId}`;

  // Only select if this check is selected
  const isSelected = expandedFindings.has(checkKey);

  const failedCount = findings.filter((f) => f.result === "FAIL").length;
  const warningCount = findings.filter(
    (f) => f.result !== "PASS" && f.result !== "FAIL",
  ).length;

  // Get description from first finding's message or category
  const description = null;

  const handleCardSelect = () => {
    setExpandedFrames(new Set()); // Clear other section selections
    setExpandedFindings(new Set([checkKey]));
  };

  return (
    <div
      className={`frame-card check-card ${isSelected ? "selected" : ""}`}
      onClick={handleCardSelect}
    >
      <div className="check-card-body">
        <div className="check-card-title">{formatCheckId(checkId)}</div>
        {description && (
          <div className="check-card-description">{description}</div>
        )}
      </div>
      <div className="check-card-header">
        <div className="frame-card-counts">
          {warningCount > 0 && (
            <span className="count-badge warning">{warningCount}</span>
          )}
          {failedCount > 0 && (
            <span className="count-badge failed">{failedCount}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// Check findings component - shows findings grouped by status with frame_id
const CheckFindings: React.FC<{
  findings: Finding[];
  isResultsComplete?: boolean;
}> = ({ findings, isResultsComplete = true }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["failed", "warnings"]),
  );

  const failedFindings = findings.filter((f) => f.result === "FAIL");
  const warningFindings = findings.filter(
    (f) => f.result !== "PASS" && f.result !== "FAIL",
  );
  const passedFindings = findings.filter((f) => f.result === "PASS");

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };



  const renderFindingsList = (findingsList: Finding[]) => {
    return findingsList.map((finding) => (
        <div key={finding.finding_id} className="status-finding-item">
          <div className="finding-header">
            <span className="finding-title">
              {finding.frame_id && (
                <>
                  Section: <span>{finding.frame_id}</span>
                </>
              )}
            </span>
          </div>
          <div className="finding-content">
            {finding.message && (
              <div className="finding-message">
                {formatMessage(finding.message)}
              </div>
            )}
            {(finding.actual || finding.expected || finding.recommendation) && (
              <div className="finding-recommendation">
                {finding.recommendation || finding.expected || finding.actual}
              </div>
            )}
          </div>
        </div>
      ));
  };

  return (
    <>
      <div className="findings-by-status">
        {failedFindings.length > 0 && (
          <div className="status-section failed">
            <div
              className="status-header"
              onClick={() => toggleSection("failed")}
            >
              <Icon
                type={
                  expandedSections.has("failed") ? "chevron-up" : "chevron-down"
                }
                size="small"
              />
              <FailIcon className="status-icon" />
              <span className="status-title">
                Failed ({failedFindings.length})
                {!isResultsComplete && (
                  <Icon
                    type="loading"
                    size="small"
                    className="spinner-loader"
                  />
                )}
              </span>
            </div>
            {expandedSections.has("failed") && (
              <div className="status-content">
                {renderFindingsList(failedFindings)}
              </div>
            )}
          </div>
        )}

        {warningFindings.length > 0 && (
          <div className="status-section warnings">
            <div
              className="status-header"
              onClick={() => toggleSection("warnings")}
            >
              <Icon
                type={
                  expandedSections.has("warnings") ? "chevron-up" : "chevron-down"
                }
                size="small"
              />
              <WarningIcon className="status-icon" />
              <span className="status-title">
                Observations ({warningFindings.length})
                {!isResultsComplete && (
                  <Icon
                    type="loading"
                    size="small"
                    className="spinner-loader"
                  />
                )}
              </span>
            </div>
            {expandedSections.has("warnings") && (
              <div className="status-content">
                {renderFindingsList(warningFindings)}
              </div>
            )}
          </div>
        )}

        {passedFindings.length > 0 && (
          <div className="status-section passed">
            <div
              className="status-header"
              onClick={() => toggleSection("passed")}
            >
              <Icon
                type={
                  expandedSections.has("passed") ? "chevron-up" : "chevron-down"
                }
                size="small"
              />
              <Icon type="check-round" size="small" className="status-icon" />
              <span className="status-title">
                Passed ({passedFindings.length})
                {!isResultsComplete && (
                  <Icon
                    type="loading"
                    size="small"
                    className="spinner-loader"
                  />
                )}
              </span>
            </div>
            {expandedSections.has("passed") && (
              <div className="status-content">
                {renderFindingsList(passedFindings)}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};